import { useQuery } from '@tanstack/react-query';
import { usePublicClient } from 'wagmi';
import { parseAbi } from 'viem';
import { queryKeys } from 'structure/pv2/vault/configs/queryKeys';
import { QueryOptions } from 'utils/useQueryUtils';
import { TOKENS } from 'utils/constants/tokens';
import { formatUnits } from 'utils/units';

const ORACLE_REGISTRY_ADDRESS = '0x561eDb11aBE844b7B5AB9334375459699c0a317b';

interface UseTokenPriceProps extends QueryOptions {
  tokenAddress: `0x${string}`;
  chainId: number;
}

export const useTokenPrice = (props: UseTokenPriceProps) => {
  const { tokenAddress, chainId, query } = props;
  const publicClient = usePublicClient({ chainId });

  const quoteAddress = TOKENS.BASE_L2.USDC.address;

  const useQueryOptions = {
    ...query,
    queryKey: queryKeys.tokenPrice({ tokenAddress, quoteAddress, chainId }),
    enabled: !!publicClient,
  };

  return useQuery({
    ...useQueryOptions,
    queryFn: async () => {
      if (!publicClient) throw new Error('Public client not found');

      const oracleAddress = await publicClient.readContract({
        abi: parseAbi([
          'function oracles(address token) view returns (address)',
        ]),
        address: ORACLE_REGISTRY_ADDRESS,
        functionName: 'oracles',
        args: [quoteAddress],
        authorizationList: undefined,
      });

      const data = await publicClient.readContract({
        abi: parseAbi([
          'function getPrice(address base, address quote) view returns (uint256 price, uint256 oldestObservation)',
        ]),
        functionName: 'getPrice',
        address: oracleAddress,
        args: [tokenAddress, quoteAddress],
        authorizationList: undefined,
      });

      const [price, oldestObservation] = data;

      return {
        price,
        decimals: TOKENS.BASE_L2.USDC.decimals,
        formatedPrice: formatUnits(price, TOKENS.BASE_L2.USDC.decimals),
        oldestObservation,
      };
    },
  });
};
